// Push Notification Utilities

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

/**
 * Convert a base64 string to a Uint8Array (required for VAPID key)
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

/**
 * Register the service worker
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) {
        console.warn('Service Worker is not supported in this browser');
        return null;
    }

    try {
        const registration = await navigator.serviceWorker.register('/service-worker.js');
        console.log('Service Worker registered successfully:', registration);
        return registration;
    } catch (error) {
        console.error('Service Worker registration failed:', error);
        return null;
    }
}

/**
 * Check if the browser supports push notifications
 */
export function isPushNotificationSupported(): boolean {
    return (
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window
    );
}

/**
 * Request notification permission from the user
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
        console.warn('Notifications are not supported in this browser');
        return 'denied';
    }

    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPushNotifications(): Promise<PushSubscription | null> {
    if (!isPushNotificationSupported()) {
        throw new Error('Push notifications are not supported in this browser');
    }

    if (!VAPID_PUBLIC_KEY) {
        throw new Error('VAPID public key is not configured. Please contact support.');
    }

    try {
        // Check current permission status
        const currentPermission = Notification.permission;
        console.log('Current notification permission:', currentPermission);

        // If already denied, provide clear message
        if (currentPermission === 'denied') {
            throw new Error('Notifications are blocked. Please enable them in your browser settings:\n\n1. Click the lock/info icon in the address bar\n2. Find "Notifications" and set to "Allow"\n3. Refresh the page and try again');
        }

        // Request permission
        const permission = await requestNotificationPermission();
        console.log('Permission request result:', permission);

        if (permission === 'denied') {
            throw new Error('You denied notification permission. To enable, click the lock icon in your address bar and allow notifications.');
        }

        if (permission !== 'granted') {
            throw new Error('Notification permission was not granted');
        }

        // Get or register service worker
        let registration = await navigator.serviceWorker.ready;
        if (!registration) {
            registration = await registerServiceWorker();
            if (!registration) {
                throw new Error('Failed to register service worker');
            }
        }

        // Check if already subscribed
        let subscription = await registration.pushManager.getSubscription();

        if (!subscription) {
            // Subscribe to push notifications
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
            });
            console.log('New push subscription created:', subscription);
        } else {
            console.log('Already subscribed to push notifications');
        }

        // Send subscription to backend
        await sendSubscriptionToBackend(subscription);

        return subscription;
    } catch (error) {
        console.error('Failed to subscribe to push notifications:', error);
        // Re-throw the error so it can be caught and displayed in the UI
        throw error;
    }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPushNotifications(): Promise<boolean> {
    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (subscription) {
            // Remove from backend first
            await removeSubscriptionFromBackend(subscription);

            // Then unsubscribe locally
            const success = await subscription.unsubscribe();
            console.log('Unsubscribed from push notifications:', success);
            return success;
        }

        return false;
    } catch (error) {
        console.error('Failed to unsubscribe from push notifications:', error);
        return false;
    }
}

/**
 * Check if the user is currently subscribed to push notifications
 */
export async function isSubscribedToPushNotifications(): Promise<boolean> {
    if (!isPushNotificationSupported()) {
        return false;
    }

    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        return subscription !== null;
    } catch (error) {
        console.error('Failed to check subscription status:', error);
        return false;
    }
}

/**
 * Send the subscription to the backend
 */
async function sendSubscriptionToBackend(subscription: PushSubscription): Promise<void> {
    const subscriptionData = subscription.toJSON();

    const response = await fetch(`${API_BASE_URL}/push/subscribe`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(subscriptionData),
    });

    if (!response.ok) {
        throw new Error('Failed to save subscription to backend');
    }

    console.log('Subscription saved to backend');
}

/**
 * Remove the subscription from the backend
 */
async function removeSubscriptionFromBackend(subscription: PushSubscription): Promise<void> {
    const subscriptionData = subscription.toJSON();

    const response = await fetch(`${API_BASE_URL}/push/unsubscribe`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            endpoint: subscriptionData.endpoint,
        }),
    });

    if (!response.ok) {
        throw new Error('Failed to remove subscription from backend');
    }

    console.log('Subscription removed from backend');
}

/**
 * Send a test notification (for testing purposes)
 */
export async function sendTestNotification(): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/push/test`, {
        method: 'POST',
        credentials: 'include',
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send test notification');
    }

    console.log('Test notification sent');
}
